import React from 'react'
import clsx from 'clsx'
import { UserAdd } from 'iconsax-react'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { Button, useTheme } from '@mui/material'

import UserSearchDropdown from '../UserSearchDropdown'
import Tooltip from '@SonarCX/components/atoms/Tooltip'
import SonarCXButton from '@SonarCX/components/atoms/Button2'

import { applyHistory } from './helpers'
import { arraysEqual } from '@SonarCX/utilities'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useStyles from './styles'

type EditUsersListProps = {
  prop: 'assignee_ids' | 'watcher_ids'
  task: LocalTask | APITask
  onChange: React.Dispatch<React.SetStateAction<string[]>>
}

const EditUsersList: React.FC<EditUsersListProps> = ({
  prop,
  task,
  onChange
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { formatTask, updateTask, setSkipUpdateID } = useTaskAPI()

  // History of select / unselects
  const [selection, setSelection] = React.useState<string[]>([])

  const usersToShow = React.useMemo(
    () => applyHistory(task[prop] ?? [], selection),
    [task, prop, selection]
  )

  const classes = useStyles()

  const handleSelect = React.useCallback((newID: string) => {
    // Add to selection history
    setSelection(old => [...old, newID])
  }, [])

  // Perform task update
  const handleSave = React.useCallback(async () => {
    try {
      setSkipUpdateID(old => [...old, task.id])
      const newTask: LocalTask = {
        ...formatTask(task),
        [prop]: usersToShow
      }
      onChange(usersToShow)
      setSelection([])

      await updateTask(newTask.id, newTask)
      dispatch(AddSuccessAlert('Changes saved'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }, [
    setSkipUpdateID,
    task,
    formatTask,
    prop,
    usersToShow,
    onChange,
    updateTask,
    dispatch
  ])

  const handleDiscard = React.useCallback(() => {
    setSelection([])
  }, [])

  return (
    <UserSearchDropdown
      selected={usersToShow}
      onSelect={handleSelect}
      onClose={handleDiscard}
      limit={10}
      multi
      prevSelected={[]}
      anchorClass={clsx(
        classes.anchor,
        task[prop].length !== 0 && classes.unpadLeft
      )}
      TopChildren={({ onClose }) => (
        <>
          <Button variant='text' onClick={onClose} className={classes.button}>
            Cancel
          </Button>

          <SonarCXButton
            onClick={() => {
              handleSave()
              onClose()
            }}
            disabled={arraysEqual(usersToShow, task[prop])}
            className={classes.button}
          >
            Save
          </SonarCXButton>
        </>
      )}
    >
      <div onClick={() => setSelection([])} className={classes.select}>
        <Tooltip
          title={`Edit${
            prop === 'assignee_ids'
              ? ' Assignees'
              : prop === 'watcher_ids'
              ? ' Watchers'
              : ''
          }`}
          arrow
        >
          <div className={classes.root}>
            <UserAdd size={theme.size.base} color='#aaa' />
          </div>
        </Tooltip>
      </div>
    </UserSearchDropdown>
  )
}

export default React.memo(EditUsersList)
